import React from "react";
import { Form } from "react-final-form";
import arrayMutators from "final-form-arrays";
import { FieldArray } from "react-final-form-arrays";
import { useHistory } from "react-router-dom";

import styles from "./PharmacyDetailComponents.module.css";
import Card from "../../../../components/Card";
import TitlePage from "../../../../components/TitlePage";
import MedicalInfoSection from "../../../../components/MedicalInfoSection";
import { TablePaymentItem } from "../../../../components/ModalDetailReservationV2/components/PaymentSection";
import Button from "../../../../components/Button";
import { formatNumberWithCurrency } from "../../../../../utils/utils";
import { getGenderLabel } from "../../../../../utils/constants";
import Spinner from "../../../../components/Spinner";
import { useModal } from "../../../../../context/ModalProvider";

export default function PharmacyDetailComponents({
  dataDetailReservation,

  // Medical data
  medicalData = {},

  // Payment data and options
  paymentData = {},
  itemOptions = [],
  packageOptions = [],

  // Loading states
  isLoadingItems = false,
  isLoadingPackages = false,
  loading = false,

  // Event handlers
  handleAddPaymentItem = () => undefined,
  handleAddPaymentPackage = () => undefined,
  handleDeletePaymentItem = () => undefined,
  handleChangePaymentForm = () => undefined,
  handleSubmit = () => undefined,

  // Utility functions
  getDiscountItems = () => [],
  getTotalAmount = () => 0,
  requiredArray = () => undefined,

  // Localization
  localize = {},
  handleSubmitPayment = () => undefined,
}) {
  const {
    visitId,
    patientId,
    patientName = "Unknown Patient",
    patientGender = "Unknown",
    doctorName = "Unknown Doctor",
    anamnesis = "",
    physicalExamination = "",
    diagnoses = [],
    uploadedFiles = [],
  } = medicalData;
  const history = useHistory();
  const modal = useModal();

  const medicineReady = dataDetailReservation.status === "MEDICINE_READY";

  return (
    <Card
      header={
        <TitlePage
          isBack
          text={`${patientName}, ${getGenderLabel(patientGender)}`}
        />
      }
    >
      <Spinner loading={loading}>
        <div className={styles.container}>
          <MedicalInfoSection
            doctorName={doctorName}
            anamnesis={anamnesis}
            physicalExamination={physicalExamination}
            diagnoses={diagnoses}
            uploadedFiles={uploadedFiles}
            onClickDetail={() =>
              history.push({
                pathname: "/active-visit/assesment",
                search: `?id=${visitId}&patientId=${patientId}`,
                state: { isView: true },
              })
            }
          />

          <Form
            onSubmit={handleSubmit}
            mutators={{
              ...arrayMutators,
            }}
            initialValues={paymentData}
            enableReinitialize={true}
          >
            {({ handleSubmit: onSubmit, values }) => (
              <div className={styles.medicineSection}>
                <div className={styles.medicineSectionContent}>
                  {/* Header */}
                  <p className={styles.sectionTitle}>
                    {localize.getText("medicine")}
                  </p>

                  {/* Medicine Table */}
                  <FieldArray name="paymentItems" validate={requiredArray}>
                    {({ fields }) => (
                      <TablePaymentItem
                        fields={fields}
                        paymentData={paymentData}
                        itemOptions={itemOptions}
                        packageOptions={packageOptions}
                        disabledForm={
                          isLoadingItems || isLoadingPackages || medicineReady
                        }
                        handleChangeItem={(index, field, value) =>
                          handleChangePaymentForm(
                            "paymentItems",
                            value,
                            field,
                            index
                          )
                        }
                        handleRemoveItem={handleDeletePaymentItem}
                      />
                    )}
                  </FieldArray>
                </div>
                <div className={styles.footer}>
                  <div className={styles.footerTop}>
                    {/* Add Medicine Button */}
                    <div className={styles.addButtonsContainer}>
                      <Button
                        leftIcon={<i className="fas fa-plus" />}
                        label={localize.getText("item")}
                        variant="outline-light"
                        className={styles.addButton}
                        onClick={handleAddPaymentItem}
                        disabled={isLoadingItems || medicineReady}
                      />
                    </div>

                    {/* Discount Summary and Total */}
                    <div className={styles.totalsContainer}>
                      {/* Show discount items */}
                      {getDiscountItems(paymentData.paymentItems || []).map(
                        (item, index) => (
                          <div key={index} className={styles.discountRow}>
                            <p className={styles.discountLabel}>
                              {localize.getText("discountFor", {
                                itemName: item.name,
                              })}
                              :
                            </p>
                            <p className={styles.discountValue}>
                              {item.discount}
                            </p>
                          </div>
                        )
                      )}

                      {/* Total Amount */}
                      <div className={styles.totalRow}>
                        <p className={styles.totalLabel}>
                          {localize.getText("total")}:
                        </p>
                        <p className={styles.totalValue}>
                          {formatNumberWithCurrency(
                            getTotalAmount(paymentData.paymentItems || [])
                          )}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Save Button */}
                  {!medicineReady && (
                    <div className="d-flex gap-3">
                      <Button
                        label={localize.getText("save")}
                        className={styles.saveButton}
                        variant="outline-primary"
                        onClick={onSubmit}
                        fullWidth
                        disabled={
                          !paymentData.paymentItems?.length ||
                          paymentData.paymentItems.every(
                            (item) => !item.item && !item.package
                          )
                        }
                      />
                      {(dataDetailReservation.status === "WAITING_MEDICINE" ||
                        dataDetailReservation.status === "MEDICINE_READY") && (
                        <Button
                          label={localize.getText("finish")}
                          onClick={() =>
                            modal.showConfirmation({
                              title: localize.getText("areYouSure"),
                              message: localize.getText(
                                "prepareMedicineFinished"
                              ),
                              onConfirm: () => {
                                handleSubmitPayment();
                              },
                            })
                          }
                          fullWidth
                          disabled={
                            !paymentData.paymentItems?.length ||
                            paymentData.paymentItems.every(
                              (item) => !item.item && !item.package
                            )
                          }
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </Form>
        </div>
      </Spinner>
    </Card>
  );
}
